import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { acquistiApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, TrendingUp, TrendingDown, AlertTriangle, ChevronRight, Scale, Wallet, Sparkles, Download } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import AcquistiHeader from '../../components/acquisti/AcquistiHeader';
import CategoriaSelect, { NESSUNA } from '../../components/acquisti/CategoriaSelect';
import CommentoMensileCard from '../../components/acquisti/CommentoMensileCard';
import { Button } from '../../components/ui/button';
import PeriodPicker from '../../components/venduto/PeriodPicker';
import AndamentoMensileCard from '../../components/acquisti/AndamentoMensileCard';
import { periodoRange, defaultSelezione } from '../../lib/venduto';
import { fmtSoldi, fmtPct, ND } from '../../lib/acquisti';

const Delta = ({ confronto }) => {
  if (!confronto?.disponibile) {
    return <p className="text-[11px] text-muted-foreground">{confronto?.label}: nessun dato</p>;
  }
  const su = confronto.delta > 0;
  return (
    <p className={`text-xs font-semibold flex items-center gap-1 ${su ? 'text-red-700' : 'text-emerald-600'}`}>
      {su ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {su ? '+' : ''}{fmtSoldi(confronto.delta)}
      {confronto.delta_pct !== null && ` (${su ? '+' : ''}${confronto.delta_pct}%)`}
      <span className="text-muted-foreground font-normal">vs {confronto.label.toLowerCase()}</span>
    </p>
  );
};

const Barra = ({ label, valore, netto, quota, onClick, testid, badge, evidenzia }) => (
  <div className="space-y-1 cursor-pointer" onClick={onClick} data-testid={testid}>
    <div className="flex items-baseline justify-between gap-2">
      <span className={`text-sm truncate ${evidenzia ? 'text-amber-700 font-semibold' : 'font-medium'}`}>{label}</span>
      <span className="text-sm font-bold tabular-nums shrink-0">{fmtSoldi(valore)}</span>
    </div>
    <div className="flex items-center gap-2">
      <div className="h-1.5 flex-1 rounded-full bg-secondary overflow-hidden">
        <div
          className={`h-full rounded-full ${evidenzia ? 'bg-amber-500' : 'bg-primary'}`}
          style={{ width: `${Math.min(quota || 0, 100)}%` }}
        />
      </div>
      <span className="text-[11px] text-muted-foreground w-10 text-right">{quota !== null && quota !== undefined ? fmtPct(quota) : ''}</span>
      {badge}
    </div>
    {netto !== null && netto !== undefined && (
      <p className="text-[11px] text-muted-foreground tabular-nums">netto IVA {fmtSoldi(netto)}</p>
    )}
  </div>
);

const AcquistiDashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.ruolo === 'admin' || user?.is_super_admin;
  const [stato, setStato] = useState(null);
  const [sel, setSel] = useState(null);
  const [dati, setDati] = useState(null);
  const [caricando, setCaricando] = useState(true);
  const [esportando, setEsportando] = useState(false);
  const [vociBancarie, setVociBancarie] = useState(false);

  useEffect(() => {
    acquistiApi.getStato().then((s) => {
      setStato(s);
      setSel(defaultSelezione(s));
    }).catch(() => setCaricando(false));
  }, []);

  const carica = useCallback(async (range) => {
    setCaricando(true);
    try {
      setDati(await acquistiApi.getDashboard(range.data_inizio, range.data_fine));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel caricamento della dashboard');
    } finally {
      setCaricando(false);
    }
  }, []);

  const range = sel ? periodoRange(sel, stato) : null;
  useEffect(() => {
    if (range) carica(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range?.data_inizio, range?.data_fine]);

  const classifica = async (fornitoreId, categoria) => {
    try {
      await acquistiApi.setCategoriaFornitore(fornitoreId, categoria === NESSUNA ? null : categoria);
      toast.success('Categoria salvata: verrà usata anche per i prossimi import');
      if (range) carica(range);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Salvataggio non riuscito');
    }
  };

  const t = dati?.totali;

  const esportaAnalisi = async () => {
    setEsportando(true);
    try {
      const file = await acquistiApi.exportAnalisi();
      toast.success(
        `Export pronto: ${file.periodo} · ${file.nomi.join(' + ')}. Caricali su ChatGPT o Claude per l'analisi.`,
        { duration: 8000 },
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export non riuscito');
    } finally {
      setEsportando(false);
    }
  };

  return (
    <div className="min-h-screen pb-24">
      <AcquistiHeader
        titolo="Dashboard acquisti"
        sottotitolo={range?.label || 'Periodo'}
        largo
        azione={(
          <div className="flex items-center gap-1.5 shrink-0">
            <Button
              variant="outline"
              size="sm"
              className="h-9 text-xs"
              onClick={esportaAnalisi}
              disabled={esportando}
              data-testid="dashboard-export-analisi"
            >
              {esportando
                ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                : <Download className="w-4 h-4 mr-1" />}
              Export
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-9 text-xs"
              onClick={() => navigate('/assistente')}
              data-testid="dashboard-apri-assistente"
            >
              <Sparkles className="w-4 h-4 mr-1 text-primary" /> Chiedi
            </Button>
          </div>
        )}
      />

      <main className="px-4 py-4 max-w-lg mx-auto space-y-4 lg:max-w-[1600px] lg:px-6 lg:grid lg:grid-cols-2 lg:gap-4 lg:space-y-0 lg:items-start 2xl:grid-cols-3">
        {sel && (
          <div className="lg:col-span-2 2xl:col-span-3">
            <PeriodPicker value={sel} onChange={setSel} archivio={stato} testid="acquisti-periodo" />
          </div>
        )}

        {caricando && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground lg:col-span-2 2xl:col-span-3">
            <Loader2 className="w-4 h-4 animate-spin" /> Caricamento…
          </div>
        )}

        {t && (
          <>
            {dati.risultato && (
              <Card
                className={`lg:col-span-2 2xl:col-span-3 ${
                  !dati.risultato.venduto_disponibile
                    ? 'border-border'
                    : dati.risultato.in_utile
                      ? 'border-emerald-500/50 bg-emerald-500/5'
                      : 'border-red-500/50 bg-red-500/5'
                }`}
                data-testid="dashboard-risultato"
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Scale className="w-4 h-4 text-primary" /> Risultato del periodo
                    {!dati.risultato.costi_completi && dati.risultato.venduto_disponibile && (
                      <Badge className="bg-amber-500 text-white text-[10px]" data-testid="risultato-parziale">
                        Dato parziale
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 space-y-3">
                  {!dati.risultato.venduto_disponibile ? (
                    <p className="text-sm text-muted-foreground" data-testid="risultato-no-venduto">
                      Nessun venduto in archivio per questo periodo: carica gli export di Cassa in
                      Cloud nella sezione Venduto per vedere utile o perdita.
                    </p>
                  ) : (
                    <>
                      <div className="space-y-3 lg:space-y-0 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] lg:gap-8">
                        <div className="grid grid-cols-2 gap-3 lg:content-start">
                        <div>
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                            {dati.risultato.in_utile ? 'Utile' : 'Perdita'} · IVA inclusa
                          </p>
                          <p
                            className={`text-2xl sm:text-3xl font-bold tabular-nums whitespace-nowrap ${dati.risultato.in_utile ? 'text-emerald-700' : 'text-red-700'}`}
                            data-testid="risultato-utile"
                          >
                            {fmtSoldi(dati.risultato.utile)}
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            margine {fmtPct(dati.risultato.margine_pct)} del venduto
                          </p>
                        </div>
                        <div>
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Netto IVA</p>
                          <p
                            className="text-xl sm:text-2xl font-bold tabular-nums text-muted-foreground whitespace-nowrap"
                            data-testid="risultato-utile-netto"
                          >
                            {fmtSoldi(dati.risultato.utile_netto)}
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            margine {fmtPct(dati.risultato.margine_netto_pct)} · vendite −{dati.risultato.aliquota_vendita}%
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-1.5 pt-2 border-t text-sm items-baseline lg:pt-0 lg:border-t-0 lg:border-l lg:pl-8 lg:content-start">
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground"></span>
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground text-right w-24">IVA incl.</span>
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground text-right w-24">Netto IVA</span>

                        <span className="text-muted-foreground">
                          Entrate
                          <span className="block text-[11px]">venduto Cassa in Cloud</span>
                        </span>
                        <span className="font-semibold tabular-nums text-right text-emerald-700" data-testid="risultato-entrate">
                          {fmtSoldi(dati.risultato.entrate)}
                        </span>
                        <span className="tabular-nums text-right text-muted-foreground">
                          {fmtSoldi(dati.risultato.entrate_netto)}
                        </span>

                        <span className="text-muted-foreground">
                          Uscite
                          <span className="block text-[11px]">fatture, personale, cassa, fissi, bancari</span>
                        </span>
                        <span className="font-semibold tabular-nums text-right text-red-700" data-testid="risultato-uscite">
                          {fmtSoldi(dati.risultato.uscite)}
                        </span>
                        <span className="tabular-nums text-right text-muted-foreground">
                          {fmtSoldi(dati.risultato.uscite_netto)}
                        </span>

                        <span className="font-semibold">Delta entrate − uscite</span>
                        <span className={`font-bold tabular-nums text-right ${dati.risultato.in_utile ? 'text-emerald-700' : 'text-red-700'}`}>
                          {fmtSoldi(dati.risultato.utile)}
                        </span>
                        <span className="tabular-nums text-right text-muted-foreground">
                          {fmtSoldi(dati.risultato.utile_netto)}
                        </span>
                      </div>
                      </div>

                      {!dati.risultato.costi_completi && (
                        <div
                          className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-2.5 text-[11px] text-amber-800 space-y-1"
                          data-testid="risultato-fonti-mancanti"
                        >
                          <p className="font-semibold flex items-center gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5" /> Nel periodo mancano alcune uscite
                          </p>
                          <p>
                            Fonti non caricate: {dati.risultato.fonti_mancanti.join(', ')}. Il risultato
                            è quindi migliore del reale: completa i caricamenti per leggerlo bene.
                          </p>
                          <button
                            type="button"
                            className="font-semibold underline"
                            onClick={() => navigate('/promemoria')}
                            data-testid="risultato-vai-promemoria"
                          >
                            Come si estraggono i dati dalle piattaforme?
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            <AndamentoMensileCard className="lg:col-span-2 2xl:col-span-3" />

            <Card data-testid="dashboard-kpi">
              <CardContent className="p-4 space-y-3">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Totale acquisti (IVA inclusa)</p>
                  <p className="text-3xl font-bold tabular-nums" data-testid="kpi-totale">{fmtSoldi(t.totale)}</p>
                  <div className="space-y-0.5 mt-1">
                    <Delta confronto={dati.confronto.precedente} />
                    <Delta confronto={dati.confronto.anno_prima} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Imponibile</p>
                    <p className="font-semibold tabular-nums" data-testid="kpi-imponibile">{fmtSoldi(t.imponibile)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">IVA</p>
                    <p className="font-semibold tabular-nums" data-testid="kpi-iva">{fmtSoldi(t.iva)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Fatture</p>
                    <p className="font-semibold tabular-nums" data-testid="kpi-fatture">
                      {t.n_documenti}
                      {t.n_note_credito > 0 && <span className="text-xs text-muted-foreground"> (+{t.n_note_credito} N.C.)</span>}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Fornitori</p>
                    <p className="font-semibold tabular-nums">{t.n_fornitori}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {dati.incidenza && (
              <Card data-testid="dashboard-incidenza">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Scale className="w-4 h-4 text-primary" /> Peso degli acquisti sul venduto
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {!dati.incidenza.venduto_disponibile ? (
                    <p className="text-sm text-muted-foreground" data-testid="incidenza-no-venduto">
                      Per questo periodo non c'è venduto in archivio: carica i dati di Cassa in Cloud
                      nella sezione Venduto per vedere l'incidenza.
                    </p>
                  ) : !dati.incidenza.acquisti_disponibile ? (
                    <p className="text-sm text-muted-foreground" data-testid="incidenza-no-acquisti">
                      Venduto del periodo {fmtSoldi(dati.incidenza.venduto_incasso)}, ma nessuna
                      fattura di acquisto in archivio per queste date: importa le fatture del periodo
                      per vedere l'incidenza.
                    </p>
                  ) : (
                    <>
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                          Acquisti food &amp; beverage sul venduto
                        </p>
                        <div className="grid grid-cols-2 gap-3 mt-1">
                          <div>
                            <p className="text-3xl font-bold tabular-nums" data-testid="incidenza-materia-prima">
                              {fmtPct(dati.incidenza.incidenza_materia_prima_pct)}
                            </p>
                            <p className="text-[11px] text-muted-foreground">IVA inclusa, su venduto lordo</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold tabular-nums text-muted-foreground" data-testid="incidenza-materia-prima-netto">
                              {fmtPct(dati.incidenza.incidenza_materia_prima_netto_pct)}
                            </p>
                            <p className="text-[11px] text-muted-foreground">
                              netto IVA, su venduto netto (−{dati.incidenza.aliquota_stima}%)
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-1.5 pt-2 border-t text-sm items-baseline">
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground"></span>
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground text-right w-24">IVA incl.</span>
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground text-right w-24">Netto IVA</span>

                        <span className="text-muted-foreground">
                          Venduto del periodo
                          <span className="block text-[11px]">
                            {dati.incidenza.venduto_giornate} giornate · {dati.incidenza.venduto_coperti} coperti
                          </span>
                        </span>
                        <span className="font-semibold tabular-nums text-right" data-testid="incidenza-venduto">
                          {fmtSoldi(dati.incidenza.venduto_incasso)}
                        </span>
                        <span className="tabular-nums text-right text-muted-foreground" data-testid="incidenza-venduto-netto">
                          {fmtSoldi(dati.incidenza.venduto_netto)}
                        </span>

                        <span className="text-muted-foreground">
                          Acquisti materia prima
                          <span className="block text-[11px]">categorie food e beverage</span>
                        </span>
                        <span className="font-semibold tabular-nums text-right">{fmtSoldi(dati.incidenza.materia_prima)}</span>
                        <span className="tabular-nums text-right text-muted-foreground">{fmtSoldi(dati.incidenza.materia_prima_netto)}</span>

                        <span className="text-muted-foreground">
                          Tutti gli acquisti
                          <span className="block text-[11px]">energia, servizi e altro inclusi</span>
                        </span>
                        <span className="font-semibold tabular-nums text-right">{fmtSoldi(dati.incidenza.acquisti_totale)}</span>
                        <span className="tabular-nums text-right text-muted-foreground">{fmtSoldi(dati.incidenza.acquisti_imponibile)}</span>

                        <span className="text-muted-foreground">Peso di tutti gli acquisti</span>
                        <span className="font-semibold tabular-nums text-right" data-testid="incidenza-acquisti">
                          {fmtPct(dati.incidenza.incidenza_acquisti_pct)}
                        </span>
                        <span className="tabular-nums text-right text-muted-foreground">
                          {fmtPct(dati.incidenza.incidenza_acquisti_netto_pct)}
                        </span>
                      </div>

                      {dati.incidenza.non_classificato > 0 && (
                        <p className="text-[11px] text-amber-700">
                          {fmtSoldi(dati.incidenza.non_classificato)} di spesa è ancora DA CLASSIFICARE:
                          la percentuale food &amp; beverage può salire una volta assegnate le categorie.
                        </p>
                      )}
                      <p className="text-[11px] text-muted-foreground leading-relaxed">
                        È il rapporto tra quello che hai <strong>comprato</strong> e quello che hai
                        venduto nel periodo: un indicatore di rotta, non il food cost reale (per
                        quello servono gli inventari).
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {dati.costi_totali && (
              <Card data-testid="dashboard-costi-totali">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-primary" /> Costi totali del periodo
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Totale IVA inclusa</p>
                      <p className="text-3xl font-bold tabular-nums" data-testid="costi-totali">
                        {fmtSoldi(dati.costi_totali.totale)}
                      </p>
                      {dati.costi_totali.incidenza_sul_venduto_pct !== null && (
                        <p className="text-[11px] text-muted-foreground">
                          {fmtPct(dati.costi_totali.incidenza_sul_venduto_pct)} del venduto lordo
                          ({fmtSoldi(dati.costi_totali.venduto_incasso)})
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Totale netto IVA</p>
                      <p className="text-2xl font-bold tabular-nums text-muted-foreground" data-testid="costi-totali-netto">
                        {fmtSoldi(dati.costi_totali.totale_netto)}
                      </p>
                      {dati.costi_totali.incidenza_netto_pct !== null && (
                        <p className="text-[11px] text-muted-foreground">
                          {fmtPct(dati.costi_totali.incidenza_netto_pct)} del venduto netto
                          ({fmtSoldi(dati.costi_totali.venduto_netto)})
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 gap-y-1.5 pt-2 border-t text-sm items-baseline">
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground"></span>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground text-right w-24">IVA incl.</span>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground text-right w-24">Netto IVA</span>
                    {dati.costi_totali.fonti.map((f) => (
                      <React.Fragment key={f.fonte}>
                        <span className="text-muted-foreground">
                          {f.label}
                          {f.fonte === 'fatture_passive' && f.n_documenti ? ` (${f.n_documenti} fatture)` : ''}
                          {f.fonte === 'uscite_cassa' && f.n_movimenti ? ` (${f.n_movimenti} movimenti)` : ''}
                          {f.fonte === 'personale' && f.ore ? ` · ${f.ore} ore` : ''}
                          {f.fonte === 'costi_fissi' && f.n_rate ? ` (${f.n_rate} rate)` : ''}
                          {f.fuori_campo_iva && <span className="block text-[11px]">fuori campo IVA</span>}
                          {f.iva_nota === false && <span className="block text-[11px]">importi pagati, IVA non scorporabile</span>}
                        </span>
                        <span className="font-semibold tabular-nums text-right" data-testid={`costi-fonte-${f.fonte}`}>
                          {fmtSoldi(f.totale)}
                        </span>
                        <span className="tabular-nums text-right text-muted-foreground" data-testid={`costi-fonte-netto-${f.fonte}`}>
                          {fmtSoldi(f.totale_netto)}
                        </span>
                      </React.Fragment>
                    ))}
                    <span className="text-[11px] text-muted-foreground col-span-3 pt-1">
                      IVA sulle fatture del periodo: {fmtSoldi(dati.costi_totali.iva_fatture)}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {dati.costi_totali.personale?.personale_extra > 0 && (
                      <p className="text-[11px] text-primary" data-testid="costi-personale-premi">
                        Personale complessivo {fmtSoldi(dati.costi_totali.personale.costo_totale)}:
                        {' '}buste paga + contributi {fmtSoldi(dati.costi_totali.personale.libro_paga)}
                        {' '}+ Personale Extra {fmtSoldi(dati.costi_totali.personale.personale_extra)}
                        {' '}({dati.costi_totali.personale.n_personale_extra} uscite di cassa)
                      </p>
                    )}
                  </div>

                  <div className="space-y-2 pt-2 border-t" data-testid="costi-uscite-cassa">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        Uscite di cassa del periodo
                      </p>
                      <button
                        onClick={() => navigate('/uscite')}
                        className="text-[11px] font-semibold underline text-primary"
                        data-testid="costi-uscite-apri"
                      >
                        Apri elenco
                      </button>
                    </div>
                    {dati.costi_totali.uscite_per_categoria?.length ? (
                      <>
                        {dati.costi_totali.uscite_per_categoria.map((u) => (
                          <div
                            key={u.categoria_uscita}
                            className="flex items-baseline justify-between gap-2 text-sm"
                            data-testid={`costi-uscita-${u.categoria_uscita.replace(/[^\w]/g, '-')}`}
                          >
                            <span>
                              {u.categoria_uscita}
                              <span className="text-muted-foreground text-[11px]"> · {u.n} mov.</span>
                              {u.personale_extra && (
                                <span className="text-[11px] block text-primary">
                                  Personale Extra: sommato al costo del personale
                                </span>
                              )}
                            </span>
                            <span className="font-semibold tabular-nums shrink-0">
                              {fmtSoldi(u.totale)}
                            </span>
                          </div>
                        ))}
                        <p className="text-[11px] text-muted-foreground">
                          {fmtSoldi(dati.costi_totali.uscite_riepilogo?.totale)} in
                          {' '}{dati.costi_totali.uscite_riepilogo?.n_movimenti} movimenti, tutti
                          conteggiati nei costi totali
                        </p>
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground" data-testid="costi-uscite-vuote">
                        Nessuna uscita di cassa registrata in questo periodo: le uscite si inseriscono a
                        mano dalla pagina Uscite di Cassa (contanti, POS, bonifici fuori fattura).
                      </p>
                    )}
                  </div>

                  <div className="space-y-2 pt-2 border-t" data-testid="costi-fissi-blocco">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        Costi fissi ricorrenti del periodo
                      </p>
                      <button
                        onClick={() => navigate('/acquisti/costi-fissi')}
                        className="text-[11px] font-semibold underline text-primary"
                        data-testid="costi-fissi-apri"
                      >
                        Gestisci
                      </button>
                    </div>
                    {dati.costi_totali.costi_fissi?.voci?.length ? (
                      <>
                        {dati.costi_totali.costi_fissi.voci.map((v) => (
                          <div
                            key={`${v.costo_id}-${v.mese}`}
                            className="flex items-baseline justify-between gap-2 text-sm"
                            data-testid={`costo-fisso-rata-${v.costo_id}-${v.mese}`}
                          >
                            <span className="min-w-0">
                              {v.nome}
                              <span className="text-muted-foreground text-[11px]">
                                {' '}· {v.categoria}
                                {v.n_rate ? ` · rata ${v.rata}/${v.n_rate}` : ''}
                              </span>
                            </span>
                            <span className="font-semibold tabular-nums shrink-0">{fmtSoldi(v.importo)}</span>
                          </div>
                        ))}
                        {dati.costi_totali.costi_fissi.doppioni?.length > 0 && (
                          <p className="text-[11px] text-amber-700" data-testid="costi-fissi-doppioni-avviso">
                            {dati.costi_totali.costi_fissi.doppioni.length} possibili doppioni con fatture
                            o uscite di cassa: controlla nella scheda Costi fissi.
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground" data-testid="costi-fissi-vuoti">
                        Nessuna rata ricorrente in scadenza in questo periodo: affitto, finanziamenti e
                        leasing si registrano nella scheda Costi fissi.
                      </p>
                    )}
                  </div>

                  <div className="space-y-2 pt-2 border-t" data-testid="bancari-blocco">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                        Costi bancari e di incasso
                      </p>
                      <button
                        onClick={() => navigate('/acquisti/bancari')}
                        className="text-[11px] font-semibold underline text-primary"
                        data-testid="bancari-apri"
                      >
                        Gestisci
                      </button>
                    </div>
                    {dati.costi_totali.costi_bancari?.disponibile ? (
                      <>
                        <button
                          onClick={() => setVociBancarie((v) => !v)}
                          className="flex items-baseline justify-between gap-2 w-full text-sm"
                          data-testid="bancari-toggle-voci"
                        >
                          <span className="text-muted-foreground">
                            {dati.costi_totali.costi_bancari.n_mesi}
                            {dati.costi_totali.costi_bancari.n_mesi === 1 ? ' mese' : ' mesi'} ·
                            {' '}{vociBancarie ? 'nascondi' : 'mostra'} le {dati.costi_totali.costi_bancari.per_voce.length} voci
                          </span>
                          <span className="font-semibold tabular-nums shrink-0" data-testid="bancari-totale-periodo">
                            {fmtSoldi(dati.costi_totali.costi_bancari.totale)}
                          </span>
                        </button>
                        {vociBancarie && dati.costi_totali.costi_bancari.per_voce.map((v) => (
                          <div
                            key={v.voce}
                            className="flex items-baseline justify-between gap-2 text-[11px]"
                            data-testid={`bancari-voce-${v.voce.replace(/[^\w]/g, '-')}`}
                          >
                            <span className="text-muted-foreground min-w-0">{v.voce}</span>
                            <span className="tabular-nums shrink-0">{fmtSoldi(v.totale)}</span>
                          </div>
                        ))}
                        {dati.costi_totali.costi_bancari.doppioni?.length > 0 && (
                          <p className="text-[11px] text-amber-700" data-testid="bancari-doppioni-avviso">
                            {dati.costi_totali.costi_bancari.doppioni.length} voci fattura POS risultano
                            anche fra le fatture elettroniche: controlla nella scheda Bancari.
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground" data-testid="bancari-vuoti">
                        Nessun riepilogo bancario caricato per questo periodo: commissioni, canoni e
                        bolli si caricano nella scheda Bancari.
                      </p>
                    )}
                  </div>

                  <div className="space-y-2 pt-2 border-t">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      Tutte le categorie di costo
                    </p>
                    {dati.costi_totali.categorie.slice(0, 10).map((c) => (
                      <Barra
                        key={c.categoria}
                        label={c.categoria}
                        valore={c.totale}
                        netto={c.totale_netto}
                        quota={c.quota_pct}
                        evidenzia={c.categoria === 'DA CLASSIFICARE'}
                        testid={`costo-cat-${c.categoria.replace(/[^\w]/g, '-')}`}
                      />
                    ))}
                  </div>

                  {dati.costi_totali.avvisi?.length > 0 && (
                    <div className="text-[11px] text-amber-700 space-y-1" data-testid="costi-avvisi">
                      {dati.costi_totali.avvisi.map((a, i) => <p key={i}>{a}</p>)}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {range && range.data_inizio.slice(0, 7) === range.data_fine.slice(0, 7) && (
              <CommentoMensileCard
                anno={Number(range.data_fine.slice(0, 4))}
                mese={Number(range.data_fine.slice(5, 7))}
                isAdmin={isAdmin}
              />
            )}

            {dati.da_classificare?.n_fornitori > 0 && (
              <Card className="border-amber-300" data-testid="dashboard-da-classificare">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2 text-amber-800">
                    <AlertTriangle className="w-4 h-4" /> {dati.da_classificare.n_fornitori} fornitori da classificare
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    {fmtSoldi(dati.da_classificare.totale)} di spesa non ancora attribuita a una categoria.
                    {isAdmin ? ' Scegli la categoria: la ricordo per i prossimi import.' : ''}
                  </p>
                  {dati.da_classificare.fornitori.map((f) => (
                    <div key={f.fornitore_id} className="flex items-center justify-between gap-2 py-1.5 border-b last:border-0">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{f.nome}</p>
                        <p className="text-[11px] text-muted-foreground">{fmtSoldi(f.totale)} · {f.n_documenti} fatture</p>
                      </div>
                      {isAdmin ? (
                        <CategoriaSelect
                          valore={null}
                          categorie={stato?.categorie || []}
                          onChange={(v) => classifica(f.fornitore_id, v)}
                          testid={`classifica-${f.fornitore_id}`}
                          className="h-8 w-[150px]"
                        />
                      ) : (
                        <Badge variant="outline" className="text-[10px]">da classificare</Badge>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            <Card data-testid="dashboard-categorie">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Spesa per categoria (IVA inclusa)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {dati.per_categoria.length === 0 && (
                  <p className="text-sm text-muted-foreground">Nessuna riga nel periodo.</p>
                )}
                {dati.per_categoria.map((c) => (
                  <Barra
                    key={c.categoria}
                    label={c.categoria}
                    valore={c.totale}
                    netto={c.totale_netto}
                    quota={c.quota_pct}
                    evidenzia={c.categoria === 'DA CLASSIFICARE'}
                    testid={`categoria-${c.categoria.replace(/[^\w]/g, '-')}`}
                  />
                ))}
                {dati.copertura_righe_pct !== null && (
                  <p className="text-[11px] text-muted-foreground pt-1">
                    Calcolata sulle righe delle fatture, IVA di riga inclusa: copre il
                    {' '}{dati.copertura_righe_pct}% dell'imponibile del periodo (il resto sono
                    arrotondamenti, bolli e spese senza dettaglio riga).
                  </p>
                )}
              </CardContent>
            </Card>

            <Card data-testid="dashboard-fornitori">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Spesa per fornitore</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {dati.per_fornitore.map((f) => (
                  <Barra
                    key={f.fornitore_id}
                    label={f.nome}
                    valore={f.totale}
                    quota={f.quota_pct}
                    onClick={() => navigate(`/acquisti/fornitori/${f.fornitore_id}`)}
                    testid={`fornitore-${f.fornitore_id}`}
                    badge={<ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
                  />
                ))}
              </CardContent>
            </Card>

            {dati.andamento?.length > 1 && (
              <Card data-testid="dashboard-andamento">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Andamento mensile (tutto l'archivio)</CardTitle>
                </CardHeader>
                <CardContent className="h-52 lg:h-64 pr-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dati.andamento} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="mese" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v) => fmtSoldi(v)} />
                      <Bar dataKey="totale" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            <p className="text-[11px] text-muted-foreground leading-relaxed lg:col-span-2 2xl:col-span-3">
              Attenzione: questi sono <strong>acquisti</strong>, non consumi. Le fatture del periodo non
              corrispondono alla materia prima effettivamente consumata: servirà l'inventario
              (iniziale + acquisti − finale) per il consumo reale.
            </p>
          </>
        )}
      </main>
    </div>
  );
};

export default AcquistiDashboardPage;
